import {
  Clock, CheckCircle, XCircle, CreditCard, FileText, Trash2, Loader2, Check, CheckCheck
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { BACKEND_URL } from '../../lib/backend';

const API_URL = BACKEND_URL;

const MsgAvatar = ({ src, name, size = 'sm' }) => {
  const sz = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
  return (
    <div className={`${sz} rounded-full bg-[#1a1a1a] border border-[#333] overflow-hidden flex items-center justify-center flex-shrink-0`}>
      {src ? <img src={`${API_URL}${src}`} alt="" className="w-full h-full object-cover" /> : <span className="text-[#666] font-medium">{(name || '?')[0].toUpperCase()}</span>}
    </div>
  );
};

const QuoteStatusBadge = ({ status }) => {
  switch (status) {
    case 'pending': return <Badge className="bg-yellow-500/10 text-yellow-500 border-0"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    case 'accepted': return <Badge className="bg-lime/10 text-lime border-0"><CheckCircle className="w-3 h-3 mr-1" />Accepted</Badge>;
    case 'declined': return <Badge className="bg-red-500/10 text-red-500 border-0"><XCircle className="w-3 h-3 mr-1" />Declined</Badge>;
    default: return null;
  }
};

export const MessageBubble = ({
  msg, isArtistView, processingQuoteId,
  onAcceptQuote, onDeclineQuote, onDeleteMessage,
}) => {
  const isOwn = (isArtistView && msg.sender_role === 'artist') || (!isArtistView && msg.sender_role === 'user');

  if (msg.message_type === 'quote') {
    const quote = msg.quote_data;
    return (
      <div className={`group/msg flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
        <div className="relative">
          <div className={`max-w-[85%] rounded-xl p-4 ${isOwn ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-lime/10 border border-lime/30'}`}>
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-purple-400" />
              <span className="font-medium text-sm">Custom Quote</span>
              <QuoteStatusBadge status={quote.status} />
            </div>
            <div className="bg-[#0a0a0a] rounded-lg p-3 mb-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-2xl font-bold text-lime">${quote.price?.toFixed(2)}</span>
                <span className="text-sm text-[#888]">{quote.delivery_days} day delivery</span>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-2">{quote.description}</p>
              {quote.buyer_message && (
                <div className="mt-2 pt-2 border-t border-[#333]">
                  <p className="text-xs text-[#666] mb-1">Message for you:</p>
                  <p className="text-sm">{quote.buyer_message}</p>
                </div>
              )}
            </div>
            {!isArtistView && quote.status === 'pending' && (
              <div className="flex gap-2">
                <Button onClick={() => onAcceptQuote(msg)} disabled={processingQuoteId === msg.id} className="btn-primary text-sm flex-1" data-testid={`accept-quote-${msg.id}`}>
                  {processingQuoteId === msg.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}Accept & Pay
                </Button>
                <Button onClick={() => onDeclineQuote(msg.id)} variant="outline" className="text-sm border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`decline-quote-${msg.id}`}>
                  <XCircle className="w-4 h-4 mr-1" />Decline
                </Button>
              </div>
            )}
            <div className="mt-2 text-xs text-[#666]">{new Date(msg.created_at).toLocaleString()}</div>
          </div>
          <button
            onClick={() => onDeleteMessage(msg.id)}
            className={`absolute top-1 ${isOwn ? 'left-[-28px]' : 'right-[-28px]'} p-1 rounded text-[#555] hover:text-red-400 hover:bg-red-400/10 opacity-0 group-hover/msg:opacity-100 transition-opacity`}
            data-testid={`delete-message-${msg.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`group/msg flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`relative flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
        {!isOwn && <MsgAvatar src={msg.sender_avatar} name={msg.sender_name} />}
        <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${isOwn ? 'bg-lime text-black rounded-br-none' : 'bg-[#1E1E1E] text-white rounded-bl-none'}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium ${isOwn ? 'text-black/70' : 'text-[#A1A1AA]'}`}>{msg.sender_name}</span>
            <span className={`text-xs ${isOwn ? 'text-black/50' : 'text-[#666]'}`}>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
          {isOwn && (
            <div className="flex items-center justify-end gap-1 mt-1 text-black/60" data-testid={`read-receipt-${msg.id}`}>
              {msg.is_read ? (
                <>
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span className="text-[10px] font-medium">Seen</span>
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span className="text-[10px]">Sent</span>
                </>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => onDeleteMessage(msg.id)}
          className="p-1 rounded text-[#555] hover:text-red-400 hover:bg-red-400/10 opacity-0 group-hover/msg:opacity-100 transition-opacity flex-shrink-0"
          data-testid={`delete-message-${msg.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
